/* Mind the Shop — motion layer v1.3
   Count-up stats, scroll reveals, hairline draw-ins, green-point settle,
   card + button micro-interactions (CSS side lives in assets/mind-the-shop.css).
   Everything fires once per element and is fully disabled under
   prefers-reduced-motion. Loaded with defer from layout/theme.liquid.
   v1.1: count-up observation delayed 400ms so stats already in view on
   load still count visibly instead of finishing during first paint.
   v1.2: added homepage stats band, product trust strip and proof band.
   v1.3: injected number spans now force font/colour/display inheritance
   inline — several widgets style every descendant <span> as a small
   label (display:block, 1.1rem, slate), which was shrinking the digits
   and breaking them onto their own line. */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;
  document.documentElement.classList.add('mts-anim');

  var init = function () {
    var main = document.getElementById('MainContent');
    if (!main) return;

    /* ---------- reveal targets ---------- */
    var reveals = [];
    var push = function (el) { if (el && reveals.indexOf(el) === -1) reveals.push(el); };

    // Theme sections — skip the first so the top of every page renders instantly
    var sections = main.querySelectorAll(':scope > .shopify-section');
    for (var i = 1; i < sections.length; i++) push(sections[i]);

    // Landing-page bands — skip the hero
    var bands = main.querySelectorAll('.mts .mts-band');
    for (var b = 1; b < bands.length; b++) push(bands[b]);

    // Rich-text pages: headings, quotes, stat grids
    main.querySelectorAll('.rte h2, .rte h3, .rte blockquote, .rte div[style*="grid-template-columns"]').forEach(push);

    // Hairline rules draw in; green points settle
    main.querySelectorAll('hr, .point').forEach(push);

    reveals.forEach(function (el) { el.classList.add('mts-reveal'); });

    // Staggered items — only inside something that will actually reveal them
    main.querySelectorAll('.mts-card, .mts-stat, .mts-bignum > div, .mts-band-stat, .mts-ts-i, .mts-proof__stat, .mts-gallery figure, .multicolumn-list__item, .grid__item.card-wrapper, div[style*="grid-template-columns"] > div')
      .forEach(function (el) {
        if (el.closest('.mts-reveal')) el.classList.add('mts-item');
      });

    // Once an item has revealed, drop its reveal classes so hover
    // micro-interactions use their own (faster) transitions.
    main.addEventListener('transitionend', function (e) {
      var t = e.target;
      if (e.propertyName === 'transform' && t.classList && t.classList.contains('mts-item')) {
        t.classList.remove('mts-item');
      }
    });

    /* ---------- count-up ---------- */
    // No page edits needed: stat numbers already live in these elements.
    var countEls = main.querySelectorAll('.mts-bignum strong, .mts-stat strong, .mts-band-stat strong, .mts-ts-i strong, .mts-proof__num, strong[style*="IBM Plex Mono"], .mts-count, [data-mts-count]');

    // Widgets often style ALL descendant spans as labels — the injected
    // number span must render exactly like the text it replaces.
    var NEUTRAL = 'font:inherit;color:inherit;letter-spacing:inherit;text-transform:inherit;display:inline;margin:0;padding:0;';

    function prep(el) {
      if (el.__mtsNums) return;
      el.__mtsNums = [];
      el.style.fontVariantNumeric = 'tabular-nums';
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (node) {
        var re = /\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?/g;
        var text = node.nodeValue, m, last = 0, found = false;
        var frag = document.createDocumentFragment();
        while ((m = re.exec(text))) {
          found = true;
          if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
          var span = document.createElement('span');
          span.style.cssText = NEUTRAL;
          span.textContent = m[0];
          span.__target = m[0];
          el.__mtsNums.push(span);
          frag.appendChild(span);
          last = m.index + m[0].length;
        }
        if (found) {
          if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
          node.parentNode.replaceChild(frag, node);
        }
      });
    }

    function run(el) {
      prep(el);
      var nums = el.__mtsNums;
      if (!nums || !nums.length) return;
      var data = nums.map(function (s) {
        var raw = s.__target;
        return {
          el: s,
          raw: raw,
          grouped: raw.indexOf(',') > -1,
          dec: (raw.split('.')[1] || '').length,
          target: parseFloat(raw.replace(/,/g, ''))
        };
      });
      var dur = 1200, t0 = null;
      function frame(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        var e = 1 - Math.pow(1 - p, 3); // ease-out cubic
        data.forEach(function (d) {
          if (p >= 1) { d.el.textContent = d.raw; return; } // land exactly on the true figure
          var v = (d.target * e).toFixed(d.dec);
          if (d.grouped) {
            var parts = v.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            v = parts.join('.');
          }
          d.el.textContent = v;
        });
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    /* ---------- observers ---------- */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('mts-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    reveals.forEach(function (el) { io.observe(el); });

    var ioCount = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        ioCount.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0.4 });
    // Delay so stats visible on first paint still count where you can see it
    setTimeout(function () {
      countEls.forEach(function (el) { ioCount.observe(el); });
    }, 400);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
